import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Marketing.css';
import { PROMO, isPromoActive } from './config/promoConfig';

/* ───────── tiny SVG icon helpers ───────── */
const StarIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" fill="#C8A55A" />
  </svg>
);

const CheckCircle = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#EAF7F0" />
    <path d="M8 12l3 3 5-5" stroke="#1D6B45" strokeWidth="2" fill="none" strokeLinecap="round" />
  </svg>
);

const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ───────── data ───────── */

/* ───────── animated count-up stat ─────────
   Uses IntersectionObserver to trigger animation on first viewport entry.
   Animates to a numeric target over `duration` ms with ease-out cubic.
   `prefix` and `suffix` wrap the number; `decimals` for fractional values.
*/
function CountUpStat({ value, prefix = '', suffix = '', decimals = 0, duration = 1600, format = 'plain' }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animate = () => {
      if (hasAnimated.current) return;
      hasAnimated.current = true;
      const start = performance.now();
      const target = value;

      const tick = (now) => {
        const elapsed = now - start;
        const t = Math.min(elapsed / duration, 1);
        const eased = easeOutCubic(t);
        setDisplay(target * eased);
        if (t < 1) requestAnimationFrame(tick);
        else setDisplay(target); // snap to exact at end
      };
      requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          animate();
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  // Format the displayed value
  let formatted;
  if (format === 'comma') {
    formatted = Math.round(display).toLocaleString();
  } else if (decimals > 0) {
    formatted = display.toFixed(decimals);
  } else {
    formatted = Math.round(display).toString();
  }

  return <span ref={ref}>{prefix}{formatted}{suffix}</span>;
}

/* ───────── observer-triggered fade-in for static text ───────── */
function FadeInText({ children, delay = 0, duration = 800 }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const hasFired = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasFired.current) {
          hasFired.current = true;
          setTimeout(() => setVisible(true), delay);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <span
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
        transition: `opacity ${duration}ms ease-out, transform ${duration}ms ease-out`,
        display: 'inline-block',
      }}
    >
      {children}
    </span>
  );
}

/* ──────────────────────────────────────────────
   NEW PRICING MODEL
   ────────────────────────────────────────────── */
const TC_8_PRICE = 60;
const TC_16_PRICE = 115;
const TC_24_PRICE = 160;
const TC_8_PER_HOUR = 90;
const TC_16_PER_HOUR = 86.25;
const TC_24_PER_HOUR = 80;
const NONMEMBER_RATE = 140;
const INCLUDED_HOURS_8 = 8;
const INCLUDED_HOURS_16 = 16;
const INCLUDED_HOURS_24 = 24;
/* ──────────────────────────────────────────────
   HOW IT WORKS — ANIMATED STEPS
   ────────────────────────────────────────────── */
const SERVICE_AREA = {
  zips: {
    // ─── NASSAU COUNTY ───
    '11001': { town: 'Floral Park', region: 'Nassau County' },
    '11010': { town: 'Franklin Square', region: 'Nassau County' },
    '11020': { town: 'Great Neck', region: 'Nassau County' },
    '11021': { town: 'Great Neck', region: 'Nassau County' },
    '11023': { town: 'Great Neck', region: 'Nassau County' },
    '11024': { town: 'Great Neck', region: 'Nassau County' },
    '11030': { town: 'Manhasset', region: 'Nassau County' },
    '11040': { town: 'New Hyde Park', region: 'Nassau County' },
    '11041': { town: 'New Hyde Park', region: 'Nassau County' },
    '11042': { town: 'New Hyde Park', region: 'Nassau County' },
    '11050': { town: 'Port Washington', region: 'Nassau County' },
    '11501': { town: 'Mineola', region: 'Nassau County' },
    '11507': { town: 'Albertson', region: 'Nassau County' },
    '11514': { town: 'Carle Place', region: 'Nassau County' },
    '11530': { town: 'Garden City', region: 'Nassau County' },
    '11542': { town: 'Glen Cove', region: 'Nassau County' },
    '11545': { town: 'Glen Head', region: 'Nassau County' },
    '11547': { town: 'Glenwood Landing', region: 'Nassau County' },
    '11548': { town: 'Greenvale', region: 'Nassau County' },
    '11554': { town: 'East Meadow', region: 'Nassau County' },
    '11560': { town: 'Locust Valley', region: 'Nassau County' },
    '11568': { town: 'Old Westbury', region: 'Nassau County' },
    '11576': { town: 'Roslyn', region: 'Nassau County' },
    '11577': { town: 'Roslyn Heights', region: 'Nassau County' },
    '11579': { town: 'Sea Cliff', region: 'Nassau County' },
    '11590': { town: 'Westbury', region: 'Nassau County' },
    '11596': { town: 'Williston Park', region: 'Nassau County' },
    '11709': { town: 'Bayville', region: 'Nassau County' },
    '11758': { town: 'Massapequa', region: 'Nassau County' },
    '11762': { town: 'Massapequa Park', region: 'Nassau County' },
    '11765': { town: 'Mill Neck', region: 'Nassau County' },
    '11771': { town: 'Oyster Bay', region: 'Nassau County' },
    '11791': { town: 'Syosset', region: 'Nassau County' },
    '11793': { town: 'Wantagh', region: 'Nassau County' },
    '11797': { town: 'Woodbury', region: 'Nassau County' },
  },
};


/* ─── Service Area Zip Checker (compact green band) ─── */

const FAQ_DATA = [
  { q: 'What areas do you serve?', a: (
    <>
      <div style={{ marginBottom: 14 }}>
        We're based on Long Island and primarily serve homes across Nassau County, with Suffolk County visits available on request.
      </div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 90px', textAlign: 'center', background: '#FAFAF7', border: '1px solid #E5E0D8', borderRadius: 10, padding: '12px 8px' }}>
          <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, color: '#1D6B45', lineHeight: 1 }}>{new Set(Object.values(SERVICE_AREA.zips).map(d => d.town)).size}</div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 600, color: '#6B6559', textTransform: 'uppercase', letterSpacing: 1, marginTop: 6 }}>Neighborhoods covered</div>
        </div>
        <div style={{ flex: '1 1 90px', textAlign: 'center', background: '#FAFAF7', border: '1px solid #E5E0D8', borderRadius: 10, padding: '12px 8px' }}>
          <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, color: '#1D6B45', lineHeight: 1 }}>{Object.keys(SERVICE_AREA.zips).length}</div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 600, color: '#6B6559', textTransform: 'uppercase', letterSpacing: 1, marginTop: 6 }}>Zip codes served</div>
        </div>
        <div style={{ flex: '1 1 90px', textAlign: 'center', background: '#FAFAF7', border: '1px solid #E5E0D8', borderRadius: 10, padding: '12px 8px' }}>
          <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, color: '#1D6B45', lineHeight: 1 }}>Same day</div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 600, color: '#6B6559', textTransform: 'uppercase', letterSpacing: 1, marginTop: 6 }}>Typical response</div>
        </div>
      </div>
    </>
  ) },
  { q: 'How does pricing work?', a: 'Two ways. For small flat-rate jobs (TV mounts, faucet swaps, doorknob installs, and similar), we publish a fixed price up front — no surprises. For larger or hourly projects, we send a written estimate after a quick site visit (or sometimes from photos alone) showing labor and materials at cost. You approve before any work starts.' },
  { q: 'What\'s the hourly rate?', a: 'Our standard rate is $100/hr for one tech. 1-hour minimum, billed in whole-hour blocks after that. Discounts available for projects of 8+ hours. No trip charges. Materials are billed at cost with photo-verified receipts — we never mark anything up.' },
  { q: 'How fast can you get someone out?', a: 'Most requests are scheduled within 24–72 hours, depending on the size of the job and what part of our service area you\'re in. We\'ll always tell you the realistic timeline up front. For urgent issues like active leaks or no-heat situations, give us a call and we\'ll move you to the front of the line.' },
  { q: 'How do I pay?', a: 'Payment is collected on-site after the work is complete and you\'re happy with it. We accept all major credit cards, debit, Zelle, check, and cash. For larger estimated projects, we may collect a deposit before the start date — that\'ll be spelled out clearly in your estimate.' },
  { q: 'Are you licensed and insured?', a: 'Fully. We carry general liability insurance, workers\' compensation, and our crew is bonded. We\'re happy to provide documentation upon request.' },
  { q: 'What if I\'m not happy with the work?', a: 'Our labor is backed by a 1-year workmanship guarantee. If something we fix breaks or wasn\'t done right within a year, we come back at no charge. (Materials are covered by their manufacturer warranties — we\'ll help you file a claim if needed.) If something\'s not right on the day of service, you don\'t pay until we make it right.' },
  { q: 'What types of repairs do you handle?', a: 'Minor plumbing (faucets, toilets, supply lines, slow drains), minor electrical (outlets, switches, fixtures, smoke detectors), drywall patching and painting, carpentry and trim, door and lock hardware, fixture installs, mounting (TVs, art, shelves, mirrors), furniture assembly, caulking, weatherstripping, and general handyman tasks.\n\nWhat we don\'t cover: licensed specialty work such as HVAC installs, full electrical panel upgrades, roofing, structural changes, or anything requiring a building permit. We can refer you to trusted vendors for these jobs.' },
  { q: 'Do you mark up materials?', a: 'No. Ever. Materials are billed at cost with photo-verified receipts. You see exactly what was purchased, what it cost, and why. If you\'d rather supply your own materials, that works too — just have them on-site before we arrive.' },
  { q: 'Can I get a quote without a site visit?', a: 'For many smaller jobs, yes — text or email us photos and we\'ll send you a price. For larger projects with more complexity (multi-room repairs, scope discovery, anything behind a wall), a quick site visit is usually faster and more accurate than going back and forth on photos. Site visits are free for projects we end up taking on.' },
  { q: 'How do I request service?', a: 'Tap "Get a free estimate" at the bottom of any page and fill out the short form — we\'ll get back to you the same day. Or call us at (516) 386-9923 if you\'d rather just talk it through.' },
];

function FaqSection() {
  const [openIndex, setOpenIndex] = useState(-1);
  return (
    <div className="faq-container">
      {FAQ_DATA.map((item, i) => (
        <div className="faq-item" key={i}>
          <div className="faq-q" onClick={() => setOpenIndex(openIndex === i ? -1 : i)}>
            <span>{item.q}</span>
            <span className={`faq-arrow${openIndex === i ? ' open' : ''}`}>+</span>
          </div>
          <div className={`faq-a${openIndex === i ? ' open' : ''}`}>
            <div className="faq-a-text">{item.a}</div>
          </div>
        </div>
      ))}
    </div>
  );
}


/* ──────────────────────────────────────────────
   HOW IT WORKS — connected process diagram
   ────────────────────────────────────────────── */
function HowItWorksSteps() {
  const STEPS = [
    {
      title: 'Send us the details',
      desc: 'Fill out an estimate request or choose flat-rate services for your visit.',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 2h6a2 2 0 0 1 2 2v2H7V4a2 2 0 0 1 2-2z"/>
          <path d="M17 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
          <path d="M9 13h6M9 17h4"/>
        </svg>
      ),
    },
    {
      title: 'Upfront pricing, always',
      desc: 'You see the price before we start. No vague phone quotes, no surprise upsells.',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/>
          <path d="M16 2v4M8 2v4M3 10h18"/>
          <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"/>
        </svg>
      ),
    },
    {
      title: "Job's done, then you pay",
      desc: "Payment on-site when you're happy. We leave like we were never there.",
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <path d="M22 4L12 14.01l-3-3"/>
        </svg>
      ),
    },
  ];

  return (
    <>
      <style>{`
        .rh-flow{margin-top:64px;position:relative}

        /* Desktop: horizontal row with connecting line */
        .rh-flow-row{display:grid;grid-template-columns:repeat(3,1fr);gap:24px;position:relative}
        .rh-flow-row::before{
          content:'';
          position:absolute;
          top:40px;
          left:16.66%;
          right:16.66%;
          height:2px;
          background:linear-gradient(to right,
            rgba(29,107,69,0) 0%,
            rgba(29,107,69,0.25) 8%,
            rgba(29,107,69,0.25) 92%,
            rgba(29,107,69,0) 100%
          );
          z-index:0;
        }

        .rh-flow-step{position:relative;z-index:1;text-align:center;display:flex;flex-direction:column;align-items:center;padding:0 16px}

        .rh-flow-circle{
          width:80px;
          height:80px;
          border-radius:50%;
          background:#1D6B45;
          color:#fff;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          margin-bottom:24px;
          box-shadow:0 8px 24px rgba(29,107,69,0.25), 0 0 0 6px #F0F0EE;
          transition:transform .25s ease, box-shadow .25s ease;
        }
        .rh-flow-step:hover .rh-flow-circle{
          transform:translateY(-3px);
          box-shadow:0 12px 30px rgba(29,107,69,0.35), 0 0 0 6px #F0F0EE;
        }

        .rh-flow-num{
          font-family:'Montserrat',sans-serif;
          font-size:11px;
          font-weight:700;
          letter-spacing:2px;
          color:#1D6B45;
          text-transform:uppercase;
          margin-bottom:8px;
        }
        .rh-flow-title{
          font-family:'DM Serif Display',Georgia,serif;
          font-size:24px;
          color:#1A1A18;
          line-height:1.15;
          margin:0 0 10px;
          font-weight:700;
          max-width:280px;
          letter-spacing:-0.3px;
        }
        .rh-flow-desc{
          font-family:'DM Sans',sans-serif;
          font-size:15px;
          color:#4A4A48;
          line-height:1.55;
          margin:0;
          max-width:280px;
        }

        /* Mobile: vertical with vertical connecting line */
        @media(max-width:760px){
          .rh-flow{margin-top:44px}
          .rh-flow-row{grid-template-columns:1fr;gap:36px;padding-left:8px}
          .rh-flow-row::before{
            top:40px;
            bottom:40px;
            left:40px;
            right:auto;
            width:2px;
            height:auto;
            background:linear-gradient(to bottom,
              rgba(29,107,69,0) 0%,
              rgba(29,107,69,0.25) 8%,
              rgba(29,107,69,0.25) 92%,
              rgba(29,107,69,0) 100%
            );
          }
          .rh-flow-step{flex-direction:row;text-align:left;align-items:flex-start;gap:20px;padding:0}
          .rh-flow-circle{
            width:64px;
            height:64px;
            margin-bottom:0;
            flex-shrink:0;
          }
          .rh-flow-circle svg{width:28px;height:28px}
          .rh-flow-content{flex:1;padding-top:8px}
          .rh-flow-num{margin-bottom:6px;font-size:10px;letter-spacing:1.6px}
          .rh-flow-title{font-size:20px;max-width:none;margin-bottom:6px}
          .rh-flow-desc{font-size:14px;max-width:none}
        }
      `}</style>
      <div className="rh-flow">
        <div className="rh-flow-row">
          {STEPS.map((s, i) => (
            <div key={i} className="rh-flow-step reveal">
              <div className="rh-flow-circle">{s.icon}</div>
              <div className="rh-flow-content">
                <div className="rh-flow-num">Step 0{i + 1}</div>
                <h3 className="rh-flow-title">{s.title}</h3>
                <p className="rh-flow-desc">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}


/* ──────────────────────────────────────────────
   THREE SERVICES CARDS — simple, clean layout
   Repairs · Upgrades · Preventive maintenance
   ────────────────────────────────────────────── */
function ThreeServicesCards() {
  const CARDS = [
    {
      title: 'Repairs',
      desc: 'Fix what\u2019s broken. Leaky faucets, sagging doors, drywall holes, faulty switches, running toilets, and the everyday issues that pile up when you own a home.',
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
        </svg>
      ),
    },
    {
      title: 'Upgrades',
      desc: 'Make it better. TV mounts, light fixture swaps, ceiling fan installs, smart thermostats, new hardware, fresh paint, and the small projects that change how a room feels.',
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v20M2 12h20M19 5l-2 2M5 19l2-2M19 19l-2-2M5 5l2 2"/>
        </svg>
      ),
    },
    {
      title: 'Preventive maintenance',
      desc: 'Stay ahead of it. Seasonal checks, caulk and weatherstripping, gutter clearing, smoke alarm batteries, exterior touch-ups, and the small things that protect your home long-term.',
      icon: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <path d="M9 12l2 2 4-4"/>
        </svg>
      ),
    },
  ];

  return (
    <>
      <style>{`
        .rh-cards{display:grid;grid-template-columns:repeat(3,1fr);gap:24px;margin-top:48px}
        .rh-card{background:#fff;border:1px solid #DCDCD8;border-radius:14px;padding:32px 28px;transition:border-color .2s ease, transform .2s ease, box-shadow .2s ease}
        .rh-card:hover{border-color:rgba(29,107,69,.35);transform:translateY(-2px);box-shadow:0 8px 24px rgba(26,26,24,.06)}
        .rh-card-icon{display:inline-flex;align-items:center;justify-content:center;width:56px;height:56px;border-radius:12px;background:rgba(29,107,69,.08);color:#1D6B45;margin-bottom:20px}
        .rh-card-title{font-family:'DM Serif Display',Georgia,serif;font-size:24px;color:#1A1A18;line-height:1.2;margin:0 0 12px}
        .rh-card-desc{font-family:'DM Sans',sans-serif;font-size:15px;color:#4A4A48;line-height:1.6;margin:0}
        @media(max-width:900px){.rh-cards{grid-template-columns:1fr;gap:16px;margin-top:32px}.rh-card{padding:24px 22px}.rh-card-icon{width:48px;height:48px;margin-bottom:14px}.rh-card-title{font-size:22px}.rh-card-desc{font-size:14px}}
      `}</style>
      <div className="rh-cards">
        {CARDS.map((card) => (
          <div key={card.title} className="rh-card reveal">
            <div className="rh-card-icon">{card.icon}</div>
            <h3 className="rh-card-title">{card.title}</h3>
            <p className="rh-card-desc">{card.desc}</p>
          </div>
        ))}
      </div>
    </>
  );
}

export default function Marketing() {
  const navRef = useRef(null);
  const reviewsScrollRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Regional pricing toggle (shared with /services via localStorage)
  const [region, setRegion] = useState(() => {
    if (typeof window === 'undefined') return 'LI';
    try {
      const v = window.localStorage.getItem('rh_pricing_region');
      return v === 'NYC' ? 'NYC' : 'LI';
    } catch { return 'LI'; }
  });
  useEffect(() => {
    try { window.localStorage.setItem('rh_pricing_region', region); } catch {}
  }, [region]);
  const hourlyRate = 100;

  /* scroll-triggered reveals */
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('visible'); obs.unobserve(e.target); }
      }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  /* sticky nav */
  useEffect(() => {
    const onScroll = () => {
      if (navRef.current) navRef.current.classList.toggle('scrolled', window.scrollY > 50);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* smooth scroll */
  const scrollTo = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const navEl = document.querySelector('.nav');
    const navHeight = navEl ? navEl.offsetHeight : 70;
    const top = el.getBoundingClientRect().top + window.pageYOffset - navHeight + 12;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <div className="marketing-page">

      {/* ━━━ NAV ━━━ */}
      <nav className="nav" ref={navRef}>
        <div className="container nav-inner">
          <div className="nav-top-row">
            <a href="#hero" onClick={(e) => scrollTo(e, 'hero')} className="nav-logo">
              <img src={require('./assets/logo2.png')} alt="Reliable Handyman" className="nav-logo-img" />
            </a>
            <div className="nav-right-group">
              <div className="nav-desktop-links">
                <a href="#how" onClick={(e) => scrollTo(e, 'how')}>How it works</a>
                <a href="#services" onClick={(e) => scrollTo(e, 'services')}>What we do</a>
                <a href="#pricing" onClick={(e) => scrollTo(e, 'pricing')}>Pricing</a>
                <a href="#reviews" onClick={(e) => scrollTo(e, 'reviews')}>Reviews</a>
                <a href="#faq" onClick={(e) => scrollTo(e, 'faq')}>FAQ</a>
                <a href="tel:+15163869923" className="nav-desktop-phone">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.33 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  (516) 386-9923
                </a>
              </div>
              <button className="nav-hamburger" onClick={() => setMobileMenuOpen(prev => !prev)} aria-label="Menu">
                {mobileMenuOpen ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Side Drawer ── */}
      <div className={`nav-drawer-overlay ${mobileMenuOpen ? 'nav-drawer-overlay-open' : ''}`} onClick={() => setMobileMenuOpen(false)} />
      <div className={`nav-drawer ${mobileMenuOpen ? 'nav-drawer-open' : ''}`}>
        <button className="nav-drawer-close" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <div className="nav-drawer-head">
          <img src={require('./assets/logo2.png')} alt="Reliable Handyman" className="nav-drawer-logo" />
        </div>

        <div className="nav-drawer-section">
          <div className="nav-drawer-section-label">Navigate</div>
          <div className="nav-drawer-links">
            <a href="#how" onClick={(e) => { scrollTo(e, 'how'); setMobileMenuOpen(false); }}>
              How it works
            </a>
            <a href="#services" onClick={(e) => { scrollTo(e, 'services'); setMobileMenuOpen(false); }}>
              What we do
            </a>
            <a href="#pricing" onClick={(e) => { scrollTo(e, 'pricing'); setMobileMenuOpen(false); }}>
              Our pricing
            </a>
            <a href="#reviews" onClick={(e) => { scrollTo(e, 'reviews'); setMobileMenuOpen(false); }}>
              Hear from our clients
            </a>
            <a href="#faq" onClick={(e) => { scrollTo(e, 'faq'); setMobileMenuOpen(false); }}>
              Common questions
            </a>
          </div>
        </div>

        <div className="nav-drawer-bottom">
          <a href="tel:+15163869923" className="nav-drawer-call">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
            (516) 386-9923
          </a>
          <div className="nav-drawer-payments">
            <img src="https://www.americanexpress.com/content/dam/amex/us/merchant/supplies-uplift/product/images/4_Card_color_horizontal.png" alt="American Express" className="nav-pay-icon" />
            <img src="https://cdn-assets.affirm.com/images/black_logo-transparent_bg.png" alt="Affirm" className="nav-pay-icon nav-pay-affirm" />
          </div>
          <div className="nav-drawer-legal">
            <Link to="/privacy" onClick={() => setMobileMenuOpen(false)}>Privacy</Link>
            <span className="nav-drawer-staff-sep">·</span>
            <Link to="/terms" onClick={() => setMobileMenuOpen(false)}>Terms</Link>
            <span>© 2026 Reliable Handyman</span>
          </div>
        </div>
      </div>

      {/* ━━━ NAV SPACER — pushes hero below fixed nav ━━━ */}
      <div className="nav-spacer-block" />

      {/* ━━━ HERO ━━━ */}
      <section id="hero">
        <style>{`
          .nav-spacer-block{height:0}

          /* ── NEW HERO — light split layout with bold green shape ── */
          .hero-light{position:relative;overflow:hidden;background:#F1F5EF;padding-top:72px}
          @media(max-width:768px){.hero-light{padding-top:62px}}
          .hero-light::before{content:'';position:absolute;inset:0;background-image:radial-gradient(circle at 1px 1px, rgba(29,107,69,0.10) 1.5px, transparent 1.5px);background-size:36px 36px;pointer-events:none;opacity:0.55;z-index:0}
          .hero-light::after{content:'';position:absolute;top:-80px;left:-80px;width:240px;height:240px;border-radius:50%;background:rgba(200,165,90,0.10);pointer-events:none;z-index:0}

          /* Decorative organic circles */
          .hero-light-orb{position:absolute;border-radius:50%;pointer-events:none;z-index:0}
          .hero-light-orb-1{top:120px;left:48%;width:80px;height:80px;background:rgba(29,107,69,0.08);filter:blur(2px)}
          .hero-light-orb-2{bottom:-60px;left:18%;width:180px;height:180px;background:rgba(29,107,69,0.06);filter:blur(8px)}
          .hero-light-orb-3{top:36%;right:-40px;width:140px;height:140px;background:rgba(200,165,90,0.08);filter:blur(6px)}
          .hero-light-orb-4{top:140px;left:8%;width:42px;height:42px;background:rgba(29,107,69,0.18);}
          .hero-light-orb-5{bottom:120px;right:38%;width:24px;height:24px;background:rgba(200,165,90,0.30);}
          .hero-light-orb-6{top:38%;left:36%;width:14px;height:14px;background:rgba(29,107,69,0.22);}
          @media(max-width:768px){
            .hero-light-orb-1{top:60px;left:60%;width:60px;height:60px}
            .hero-light-orb-2{bottom:-40px;left:10%;width:120px;height:120px}
            .hero-light-orb-3{display:none}
            .hero-light-orb-4{top:80px;left:6%;width:28px;height:28px}
            .hero-light-orb-5{display:none}
            .hero-light-orb-6{display:none}
          }

          /* Services marquee — inside hero, extends background */
          .rh-marquee{
            position:relative;
            z-index:1;
            padding:22px 0 32px;
            overflow:hidden;
            margin-top:-24px;
          }
          .rh-marquee::before,
          .rh-marquee::after{
            content:'';
            position:absolute;
            top:0;
            bottom:0;
            width:80px;
            z-index:2;
            pointer-events:none;
          }
          .rh-marquee::before{
            left:0;
            background:linear-gradient(to right, #F1F5EF 0%, rgba(241,245,239,0) 100%);
          }
          .rh-marquee::after{
            right:0;
            background:linear-gradient(to left, #F1F5EF 0%, rgba(241,245,239,0) 100%);
          }
          .rh-marquee-track{
            display:flex;
            gap:14px;
            width:max-content;
            animation: rh-marquee-scroll 60s linear infinite;
          }
          @keyframes rh-marquee-scroll{
            0%{ transform:translateX(0); }
            100%{ transform:translateX(-50%); }
          }
          .rh-marquee-chip{
            display:inline-flex;
            align-items:center;
            gap:8px;
            background:#fff;
            border:1px solid #E5E2DC;
            border-radius:100px;
            padding:10px 18px 10px 14px;
            font-family:'Montserrat',sans-serif;
            font-size:14px;
            font-weight:600;
            color:#2A2823;
            letter-spacing:0.1px;
            white-space:nowrap;
            box-shadow:0 2px 8px rgba(26,26,24,0.06), 0 1px 2px rgba(26,26,24,0.04);
            flex-shrink:0;
          }
          .rh-marquee-chip svg{
            color:#1D6B45;
            flex-shrink:0;
          }
          @media(prefers-reduced-motion: reduce){
            .rh-marquee-track{ animation:none; }
          }
          @media(max-width:680px){
            .rh-marquee{padding:18px 0 28px;margin-top:-12px}
            .rh-marquee-track{gap:10px;animation-duration:45s}
            .rh-marquee-chip{font-size:13px;padding:8px 14px 8px 12px}
            .rh-marquee::before,
            .rh-marquee::after{width:40px}
          }
          .hero-light-inner{position:relative;z-index:1;padding:56px 0 60px;display:grid;grid-template-columns:1fr 1fr;gap:60px;align-items:center}
          .hero-light-content{display:flex;flex-direction:column;align-items:flex-start;text-align:left;max-width:560px}
          .hero-light h1{font-family:'DM Serif Display',serif;font-size:78px;line-height:1.05;color:#1A1A18;font-weight:400;margin:0 0 24px;letter-spacing:-0.5px}
          .hero-light-h1-line1{display:block;color:#1A1A18}
          .hero-light-h1-line2{display:block;color:#1D6B45;font-style:italic}
          .hero-light-sub{font-family:'Montserrat',sans-serif;font-size:17px;font-weight:400;color:#4A4A48;line-height:1.55;margin:0 0 32px;max-width:480px}
          .hero-light-ctas{display:flex;gap:12px;margin-bottom:20px;flex-wrap:wrap}
          .hero-light-call{font-family:'Montserrat',sans-serif;font-size:15px;color:#6B6559;margin-bottom:24px;font-weight:500}
          .hero-light-call a{color:#1D6B45;font-weight:700;text-decoration:none;transition:color .15s ease}
          .hero-light-call a:hover{color:#155535;text-decoration:underline}
          .hero-light-btn-primary{background:#1D6B45;color:#fff !important;padding:15px 28px;border-radius:100px;font-family:'Montserrat',sans-serif;font-size:14px;font-weight:700;letter-spacing:0.3px;text-decoration:none;display:inline-flex;align-items:center;gap:8px;transition:background .2s, transform .15s ease, box-shadow .2s ease;border:none;box-shadow:0 6px 22px rgba(29,107,69,0.40), 0 2px 6px rgba(29,107,69,0.18)}
          .hero-light-btn-primary:hover{background:#155535;transform:translateY(-1px);box-shadow:0 10px 28px rgba(29,107,69,0.50), 0 3px 8px rgba(29,107,69,0.22)}
          .hero-light-btn-secondary{background:#6dc171;color:#fff !important;padding:15px 28px;border-radius:100px;font-family:'Montserrat',sans-serif;font-size:14px;font-weight:700;letter-spacing:0.3px;border:none;text-decoration:none;display:inline-flex;align-items:center;gap:8px;transition:background .2s, transform .15s ease, box-shadow .2s ease;cursor:pointer;box-shadow:0 6px 22px rgba(109,193,113,0.45), 0 2px 6px rgba(109,193,113,0.20)}
          .hero-light-btn-secondary:hover{background:#5ab362;color:#fff !important;transform:translateY(-1px);box-shadow:0 10px 28px rgba(109,193,113,0.55), 0 3px 8px rgba(109,193,113,0.25)}
          .hero-light-trust{display:flex;align-items:center;gap:14px;flex-wrap:wrap;margin-bottom:10px}
          .hero-light-stars{display:inline-flex;align-items:center;gap:2px}
          .hero-light-stars svg{color:#C8A55A;flex-shrink:0}
          .hero-light-trust-text{font-family:'Montserrat',sans-serif;font-size:14px;color:#4A4A48;font-weight:500}
          .hero-light-trust-text strong{color:#1A1A18;font-weight:700}
          .hero-light-trust-divider{color:#C7C2B8;margin:0 4px}
          .hero-light-creds{display:flex;align-items:center;gap:10px;flex-wrap:wrap;font-family:'Montserrat',sans-serif;font-size:13px;font-weight:500;color:#6B6559;letter-spacing:0.1px}
          .hero-light-creds-sep{color:#C7C2B8}

          /* ── Right side — image area with bold green shape ── */
          .hero-light-visual{position:relative;width:100%;aspect-ratio:1/1;max-width:560px;justify-self:end}
          .hero-light-visual::before{content:'';position:absolute;top:-32px;left:-32px;right:32px;bottom:32px;background:#1D6B45;border-radius:32px;z-index:0;box-shadow:0 24px 60px rgba(29,107,69,0.28)}
          .hero-light-visual::after{display:none}
          .hero-light-image{position:absolute;inset:0;border-radius:20px;overflow:hidden;background:#F5F2EC;display:flex;align-items:center;justify-content:center;box-shadow:0 12px 40px rgba(26,26,24,0.12);z-index:1}
          .hero-light-image-placeholder{color:#C7C2B8}
          .hero-light-image img{width:100%;height:100%;object-fit:cover;object-position:center left;display:block}

          @media(max-width:980px){
            .hero-light h1{font-size:62px}
            .hero-light-inner{gap:40px;padding:40px 0 80px}
          }

          @media(max-width:768px){
            .hero-light-inner{grid-template-columns:1fr;gap:48px;padding:36px 20px 80px}
            .hero-light-content{max-width:none;align-items:center;text-align:center}
            .hero-light h1{font-size:64px}
            .hero-light-h1-line1, .hero-light-h1-line2{display:block}
            .hero-light-sub{font-size:15px;max-width:none}
            .hero-light-ctas{width:100%;flex-direction:column;gap:10px}
            .hero-light-ctas a{justify-content:center;width:100%}
            .hero-light-visual{max-width:100%;justify-self:stretch;aspect-ratio:4/3}
            .hero-light-visual::before{top:-20px;left:0;right:20px;bottom:0;border-radius:22px}
            .hero-light-image{left:20px;right:0;top:0;bottom:-20px;border-radius:16px}
            .hero-light-visual::after{width:44px;height:44px;top:-8px;right:-8px}
            .hero-light-call{text-align:center;font-size:14px}
            .hero-light-trust{gap:10px;justify-content:center}
            .hero-light-trust-text{font-size:13px}
            .hero-light-creds{font-size:12px;gap:8px;justify-content:center}
          }

          @media(max-width:480px){
            .hero-light h1{font-size:49px}
          }

          @media(max-width:360px){
            .hero-light h1{font-size:43px}
          }
        `}</style>
        <div className="hero-light">
          <div className="hero-light-orb hero-light-orb-1" />
          <div className="hero-light-orb hero-light-orb-2" />
          <div className="hero-light-orb hero-light-orb-3" />
          <div className="hero-light-orb hero-light-orb-4" />
          <div className="hero-light-orb hero-light-orb-5" />
          <div className="hero-light-orb hero-light-orb-6" />
          <div className="container hero-light-inner">

            {/* ── LEFT: content ── */}
            <div className="hero-light-content">
              <h1>
                <span className="hero-light-h1-line1">Fixed right,</span>{' '}
                <span className="hero-light-h1-line2">priced fair.</span>
              </h1>

              <p className="hero-light-sub">
                Flat-rate pricing on common household fixes &mdash; free written estimates for everything else. No trip charges, no markups, no surprise upsells.
              </p>

              <div className="hero-light-ctas">
                <a href="#pricing" onClick={(e) => scrollTo(e, 'pricing')} className="hero-light-btn-primary">
                  Get a free estimate <ArrowRight />
                </a>
                <a href="#pricing" onClick={(e) => scrollTo(e, 'pricing')} className="hero-light-btn-secondary">
                  See flat-rate pricing <ArrowRight />
                </a>
              </div>

              <div className="hero-light-call">
                Or call us anytime &middot;{' '}
                <a href="tel:+15163869923">(516) 386-9923</a>
              </div>

              <div className="hero-light-trust">
                <div className="hero-light-stars" aria-label="4.8 stars">
                  {[0,1,2,3,4].map(i => (
                    <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                </div>
                <span className="hero-light-trust-text">
                  <strong>4.8 average rating</strong> for 10+ years
                </span>
              </div>

              <div className="hero-light-creds">
                <span className="hero-light-creds-item">Licensed &amp; insured</span>
                <span className="hero-light-creds-sep">&middot;</span>
                <span className="hero-light-creds-item">No trip charges</span>
                <span className="hero-light-creds-sep">&middot;</span>
                <span className="hero-light-creds-item">No markups</span>
              </div>
            </div>

            {/* ── RIGHT: image / placeholder ── */}
            <div className="hero-light-visual">
              <div className="hero-light-image">
                <img src={require('./assets/hero-image.jpg')} alt="Reliable Handyman tech in uniform with tool belt and clipboard in a Long Island home" />
              </div>

              {/* image fills container */}
            </div>

          </div>

          {/* ━━━ SERVICES MARQUEE — extends hero background ━━━ */}
          <div className="rh-marquee">
            {(() => {
              const SERVICES = [
                'TV mounting',
                'Faucet replacement',
                'Drywall patching',
                'Light fixture install',
                'Ceiling fans',
                'Garbage disposals',
                'Doorknobs & locks',
                'Smart thermostats',
                'Caulk & grout',
                'Paint touch-ups',
                'Furniture assembly',
                'Curtain rods',
                'Toilet repair',
                'Smoke alarms',
                'Outlet replacement',
                'Cabinet hardware',
              ];
              const renderChip = (label, idx) => (
                <span key={idx} className="rh-marquee-chip">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  {label}
                </span>
              );
              return (
                <div className="rh-marquee-track">
                  {SERVICES.map((s, i) => renderChip(s, `a-${i}`))}
                  {SERVICES.map((s, i) => renderChip(s, `b-${i}`))}
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      {/* ━━━ VIDEO ━━━ */}
      {/* ━━━ HOW IT WORKS ━━━ */}
      <section className="section" id="how" style={{ background: '#F0F0EE' }}>
        <div className="container">
          <div className="section-header">
            <div className="section-eyebrow reveal">How it works</div>
            <h2 className="section-title reveal">Simple, predictable, reliable home care</h2>
          </div>
          <HowItWorksSteps />
        </div>
      </section>

      {/* ━━━ SERVICES ━━━ */}
      <section className="services-section" id="services">
        <div className="container">
          <div className="section-header">
            <div className="section-eyebrow reveal">What we do</div>
            <h2 className="section-title reveal" style={{ color: '#1A1A18' }}>
              Repairs, upgrades &amp;<br className="break-mobile-only" />{' '}preventive maintenance
            </h2>
          </div>
          <ThreeServicesCards />
        </div>
      </section>

      {/* ━━━ VIDEO BREAK ━━━ */}
      <div className="mkt-photo-break" style={{ position: 'relative', overflow: 'hidden' }}>
        <video
          autoPlay
          loop
          muted
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }}
        >
          <source src={require('./assets/handyman-video.mov')} type="video/quicktime" />
          <source src={require('./assets/handyman-video.mp4')} type="video/mp4" />
        </video>
        <div className="mkt-photo-break-overlay" />
        <div className="mkt-photo-break-text">Your Home, Handled.</div>
      </div>

      {/* ━━━ PRICING ━━━ */}
      <section className="section" id="pricing" style={{ paddingTop: 'clamp(40px, 6vw, 80px)', paddingBottom: 'clamp(40px, 6vw, 80px)', scrollMarginTop: '88px' }}>
        <div className="container">
          <div className="section-header">
            <div className="section-eyebrow reveal">Our Pricing</div>
            <h2 className="section-title reveal">Straightforward, no surprises</h2>
            <p className="section-subtitle reveal">
              Two ways to get your project done &mdash; whichever fits the job. No trip charges, no markup on materials, no upsells. You only pay when the work is done and you&rsquo;re happy with it.
            </p>

            {isPromoActive() && (
              <div className="rh-pricing-promo-wrap reveal">
                <div className="rh-pricing-promo">
                  <div className="rh-pricing-promo__tag">{PROMO.eyebrowLabel}</div>
                  <h3 className="rh-pricing-promo__headline">
                    New customers get <strong>{PROMO.percentOff}% off</strong> their first service
                  </h3>
                  <p className="rh-pricing-promo__body">
                    Up to <strong>${PROMO.capDollars} off</strong> &mdash; applied when payment is collected. Offer ends {PROMO.endDateLabel}.
                  </p>
                </div>
              </div>
            )}

            {/* Region toggle — hidden for now, restore by flipping flag */}
            {false && (
              <div className="rh-region-toggle reveal" role="tablist" aria-label="Pricing region">
                <span className="rh-region-label">Pricing for:</span>
                <button
                  type="button"
                  role="tab"
                  aria-selected={region === 'LI'}
                  className={`rh-region-pill ${region === 'LI' ? 'rh-region-pill--active' : ''}`}
                  onClick={() => setRegion('LI')}
                >
                  Long Island
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={region === 'NYC'}
                  className={`rh-region-pill ${region === 'NYC' ? 'rh-region-pill--active' : ''}`}
                  onClick={() => setRegion('NYC')}
                >
                  NYC
                </button>
              </div>
            )}
          </div>
          <style>{`
            .rh-region-toggle{display:inline-flex;align-items:center;gap:10px;margin-top:28px;padding:4px;background:#F0EDE6;border-radius:999px;border:1px solid #E5E2DC}
            .rh-region-label{font-family:'Montserrat',sans-serif;font-size:11px;font-weight:700;color:#6B6559;text-transform:uppercase;letter-spacing:1.2px;padding:0 8px 0 12px}
            .rh-region-pill{font-family:'Montserrat',sans-serif;font-size:13px;font-weight:600;color:#4A4A48;background:transparent;border:none;padding:8px 16px;border-radius:999px;cursor:pointer;transition:background .15s, color .15s}
            .rh-region-pill:hover:not(.rh-region-pill--active){color:#1A1A18}
            .rh-region-pill--active{background:#1D6B45;color:#fff}
            @media(max-width:480px){
              .rh-region-toggle{gap:4px}
              .rh-region-label{font-size:10px;padding:0 4px 0 10px;letter-spacing:1px}
              .rh-region-pill{font-size:12px;padding:7px 12px}
            }
            .rh-pricing-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px;max-width:860px;margin:40px auto 0;align-items:stretch}
            .rh-pricing-card{background:#fff;border:1px solid #E5E0D8;border-radius:14px;padding:28px 26px;display:flex;flex-direction:column;position:relative;transition:transform .25s ease, box-shadow .25s ease}
            .rh-pricing-card:hover{transform:translateY(-3px);box-shadow:0 14px 28px rgba(26,26,24,.06)}
            .rh-pricing-eyebrow{font-family:'Montserrat',sans-serif;font-size:10px;font-weight:700;color:#1D6B45;text-transform:uppercase;letter-spacing:1.6px;margin-bottom:10px}
            .rh-pricing-title{font-family:'DM Serif Display',serif;font-size:24px;color:#1A1A18;line-height:1.2;margin-bottom:8px;font-weight:700}
            .rh-pricing-price{font-family:'Montserrat',sans-serif;font-size:26px;color:#1D6B45;line-height:1;margin-bottom:4px;font-weight:500;letter-spacing:-0.01em}
            .rh-pricing-price-suffix{font-family:'Montserrat',sans-serif;font-size:13px;color:#6B6559;font-weight:500;margin-left:5px}
            .rh-pricing-desc{font-family:'Montserrat',sans-serif;font-size:13.5px;color:#4A453E;line-height:1.6;margin:14px 0 18px}
            .rh-pricing-list{list-style:none;padding:0;margin:0 0 24px;display:flex;flex-direction:column;gap:8px}
            .rh-pricing-list li{font-family:'Montserrat',sans-serif;font-size:12.5px;color:#4A453E;display:flex;align-items:flex-start;gap:9px;line-height:1.5}
            .rh-pricing-cta{margin-top:auto}
            .rh-pricing-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;background:#1D6B45;color:#fff !important;padding:12px 22px;border-radius:8px;font-family:'Montserrat',sans-serif;font-size:13.5px;font-weight:600;text-decoration:none;transition:background .2s;border:none;cursor:pointer;width:100%}
            .rh-pricing-btn:hover{background:#166038;color:#fff !important}
            .rh-pricing-btn--outline{background:transparent;color:#1A1A18 !important;border:1px solid #1A1A18}
            .rh-pricing-btn--outline:hover{background:#1A1A18;color:#fff !important}
            .rh-pricing-note{font-family:'Montserrat',sans-serif;font-size:12px;color:#9B9487;text-align:center;margin-top:28px;line-height:1.6}
            @media(max-width:760px){
              .rh-pricing-grid{grid-template-columns:1fr;gap:16px;margin-top:28px;max-width:480px}
              .rh-pricing-card{padding:24px 22px}
              .rh-pricing-title{font-size:22px}
              .rh-pricing-price{font-size:24px}
            }
          `}</style>
          <div className="rh-pricing-grid">
            <div className="rh-pricing-card reveal">
              <div className="rh-pricing-eyebrow">Fixed pricing</div>
              <div className="rh-pricing-title">Flat-rate service</div>
              <div>
                <span className="rh-pricing-price">From $50</span>
              </div>
              <p className="rh-pricing-desc">
                Pre-priced for common jobs &mdash; TV mounts, faucet swaps, doorknob installs, drywall patches, and dozens of other quick fixes. You pick the job, we book the slot.
              </p>
              <ul className="rh-pricing-list">
                <li><CheckCircle /> Fixed price up front &mdash; no hourly meter</li>
                <li><CheckCircle /> Most jobs finished in one visit</li>
                <li><CheckCircle /> Materials at cost, no markup</li>
                <li><CheckCircle /> Payment collected on-site after completion</li>
              </ul>
              <div className="rh-pricing-cta">
                <Link to="/services" className="rh-pricing-btn">
                  See flat-rate services <ArrowRight />
                </Link>
              </div>
            </div>
            <div className="rh-pricing-card reveal">
              <div className="rh-pricing-eyebrow">Variable pricing</div>
              <div className="rh-pricing-title">Hourly &amp; estimated work</div>
              <div>
                <span className="rh-pricing-price">${hourlyRate}<span className="rh-pricing-price-suffix">/hr</span></span>
              </div>
              <p className="rh-pricing-desc">
                For multi-task visits, scope discovery, or larger projects. We send a written estimate after a quick walk-through (or from photos for simpler jobs), then book the work once you approve.
              </p>
              <ul className="rh-pricing-list">
                <li><CheckCircle /> Written estimate before any work starts</li>
                <li><CheckCircle /> 1-hour minimum, then billed in whole-hour blocks</li>
                <li><CheckCircle /> Discounts available for projects of 8+ hours</li>
                <li><CheckCircle /> Materials at cost with photo receipts</li>
                <li><CheckCircle /> No trip charges, no minimums beyond hour one</li>
              </ul>
              <div className="rh-pricing-cta">
                <Link to="/estimate" className="rh-pricing-btn rh-pricing-btn--outline">
                  Request an Estimate <ArrowRight />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ TESTIMONIALS ━━━ */}
      <section className="section rh-reviews" id="reviews">
        <style>{`
          .rh-reviews{background:#F0F0EE;padding-top:clamp(64px,7vw,96px);padding-bottom:clamp(64px,7vw,96px)}
          .rh-reviews-header{display:flex;justify-content:space-between;align-items:flex-end;gap:32px;margin-bottom:48px;flex-wrap:wrap}
          .rh-reviews-header-text{flex:1;min-width:280px}
          .rh-reviews-eyebrow{font-family:'Montserrat',sans-serif;font-size:12px;font-weight:700;color:#1D6B45;text-transform:uppercase;letter-spacing:2px;margin-bottom:14px}
          .rh-reviews-title{font-family:'DM Serif Display',Georgia,serif;font-size:clamp(34px,4vw,46px);color:#1A1A18;line-height:1.1;margin:0;font-weight:400}
          .rh-reviews-badge{display:inline-flex;align-items:center;gap:10px;flex-shrink:0}
          .rh-reviews-badge-stars{display:inline-flex;gap:2px;color:#C8A55A}
          .rh-reviews-badge-num{font-family:'Montserrat',sans-serif;font-size:18px;font-weight:700;color:#1A1A18}
          .rh-reviews-badge-sub{font-family:'Montserrat',sans-serif;font-size:13px;color:#6B6559}

          .rh-reviews-grid{display:flex;gap:20px;overflow-x:auto;overflow-y:hidden;scroll-snap-type:x mandatory;scroll-behavior:smooth;-webkit-overflow-scrolling:touch;padding:4px 0 4px;margin:0 -24px;padding-left:24px;padding-right:24px;scrollbar-width:none}
          .rh-reviews-grid::-webkit-scrollbar{display:none}
          .rh-review-card{background:#fff;border:1px solid #E5E2DC;border-radius:14px;padding:24px;display:flex;flex-direction:column;flex:0 0 340px;scroll-snap-align:start;transition:border-color .2s ease,transform .2s ease,box-shadow .2s ease}
          .rh-review-card:hover{border-color:rgba(29,107,69,.25);transform:translateY(-2px);box-shadow:0 8px 24px rgba(26,26,24,.05)}

          .rh-reviews-scroller{position:relative}
          .rh-reviews-arrow{position:absolute;top:50%;transform:translateY(-50%);z-index:5;width:48px;height:48px;border-radius:50%;border:1px solid #D4DFD2;background:#fff;color:#1D6B45;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 4px 16px rgba(26,26,24,0.10), 0 1px 3px rgba(26,26,24,0.06);transition:background .15s ease,border-color .15s ease,color .15s ease,box-shadow .15s ease}
          .rh-reviews-arrow--left{left:-8px}
          .rh-reviews-arrow--right{right:-8px}
          .rh-reviews-arrow:hover{background:#1D6B45;border-color:#1D6B45;color:#fff;box-shadow:0 6px 20px rgba(29,107,69,0.30)}
          .rh-reviews-arrow:disabled{opacity:0.4;cursor:not-allowed;background:#fff;border-color:#D4DFD2;color:#1D6B45}
          .rh-review-stars{display:inline-flex;gap:2px;color:#C8A55A;margin-bottom:14px}
          .rh-review-quote{font-family:'DM Sans',sans-serif;font-size:15px;line-height:1.55;color:#2A2823;margin:0 0 20px;flex:1}
          .rh-review-author{display:flex;align-items:center;gap:12px;margin-top:auto}
          .rh-review-avatar{width:38px;height:38px;border-radius:50%;background:#1D6B45;color:#FDFCFA;display:flex;align-items:center;justify-content:center;font-family:'Montserrat',sans-serif;font-size:13px;font-weight:700;flex-shrink:0}
          .rh-review-author-text{display:flex;flex-direction:column;line-height:1.3;min-width:0}
          .rh-review-author-name{font-family:'Montserrat',sans-serif;font-size:14px;font-weight:700;color:#1A1A18}
          .rh-review-author-loc{font-family:'Montserrat',sans-serif;font-size:12px;color:#6B6559}

          @media(max-width:900px){
            .rh-reviews-header{margin-bottom:36px}
          }
          @media(max-width:600px){
            .rh-review-card{flex:0 0 280px;padding:20px}
            .rh-review-quote{font-size:14.5px}
            .rh-reviews-header{flex-direction:column;align-items:flex-start;gap:16px}
            .rh-reviews-badge{align-self:flex-start}
            .rh-reviews-grid{margin:0;padding-left:32px;padding-right:32px;gap:14px}
            .rh-reviews-arrow{display:none}
          }
        `}</style>
        <div className="container">
          <div className="rh-reviews-header">
            <div className="rh-reviews-header-text">
              <div className="rh-reviews-eyebrow">Neighbors say</div>
              <h2 className="rh-reviews-title">Built on referrals</h2>
            </div>
            <div className="rh-reviews-badge">
              <span className="rh-reviews-badge-stars">
                {[0,1,2,3,4].map(i => (
                  <svg key={i} width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ))}
              </span>
              <span className="rh-reviews-badge-num">4.8</span>
              <span className="rh-reviews-badge-sub">average for 10+ years</span>
            </div>
          </div>

          <div className="rh-reviews-scroller">
            <button
              type="button"
              aria-label="Previous reviews"
              className="rh-reviews-arrow rh-reviews-arrow--left"
              onClick={() => {
                const el = reviewsScrollRef.current;
                if (el) el.scrollBy({ left: -360, behavior: 'smooth' });
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <button
              type="button"
              aria-label="Next reviews"
              className="rh-reviews-arrow rh-reviews-arrow--right"
              onClick={() => {
                const el = reviewsScrollRef.current;
                if (el) el.scrollBy({ left: 360, behavior: 'smooth' });
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 6l6 6-6 6"/>
              </svg>
            </button>

            <div className="rh-reviews-grid" ref={reviewsScrollRef}>
            {[
              { quote: "Texted me a 30-minute window and actually hit it. Fixed two faucets and a wobbly door for exactly the quote.", initials: "DP", name: "Dana P.", loc: "Hicksville" },
              { quote: "No trip charge, no upsell. He told me which repair could wait and saved me money. That's rare.", initials: "ML", name: "Marcus L.", loc: "Garden City" },
              { quote: "Mounted the TV, hid every wire, hauled the box away. Looks like it was always there.", initials: "PS", name: "Priya S.", loc: "Massapequa" },
              { quote: "Came out the next morning for a leaking shut-off valve. Quick, clean, knew exactly what he was doing. Will absolutely call again.", initials: "JC", name: "Jen C.", loc: "Manhasset" },
              { quote: "I've called three handymen over the years and this is the first one I'll keep using. Patched ceiling drywall and you can't tell where the damage was.", initials: "RM", name: "Rob M.", loc: "Levittown" },
              { quote: "Hung a heavy gallery wall in our hallway. Levels, spacing, everything was perfect. Plus he cleaned up better than I would have.", initials: "AK", name: "Alicia K.", loc: "Roslyn" },
              { quote: "Replaced the kitchen faucet and disposal in one visit. Walked me through everything and the price was exactly what he quoted on the phone.", initials: "TH", name: "Tom H.", loc: "Mineola" },
              { quote: "Honest pricing and didn't try to talk me into a bigger project. Fixed the cabinet hinges and was out in 45 minutes.", initials: "BS", name: "Beth S.", loc: "Great Neck" },
              { quote: "Showed up, did the work, sent a clean invoice the same day. I wish more contractors operated like this.", initials: "EW", name: "Ethan W.", loc: "Syosset" },
              { quote: "Patched a hole my kid put in the wall and matched the paint perfectly. You really can't see it. Coming back for the bathroom next.", initials: "NG", name: "Nicole G.", loc: "Port Washington" },
            ].map((r, i) => (
              <div key={i} className="rh-review-card reveal">
                <div className="rh-review-stars">
                  {[0,1,2,3,4].map(j => (
                    <svg key={j} width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                </div>
                <p className="rh-review-quote">&ldquo;{r.quote}&rdquo;</p>
                <div className="rh-review-author">
                  <div className="rh-review-avatar">{r.initials}</div>
                  <div className="rh-review-author-text">
                    <span className="rh-review-author-name">{r.name}</span>
                    <span className="rh-review-author-loc">{r.loc}</span>
                  </div>
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ FAQ ━━━ */}
      <section className="section" id="faq" style={{ background: '#FFFFFF' }}>
        <style>{`
          .faq-container{max-width:720px;margin:0 auto}
          .faq-item{border-bottom:1px solid #E5E0D8}
          .faq-item:last-child{border-bottom:none}
          .faq-q{display:flex;justify-content:space-between;align-items:center;padding:18px 0;cursor:pointer;font-family:'Montserrat',sans-serif;font-size:14px;font-weight:600;color:#1A1A18;gap:16px;user-select:none}
          .faq-q:hover{color:#1D6B45}
          .faq-arrow{transition:transform .3s;font-size:18px;color:#9B9487;flex-shrink:0}
          .faq-arrow.open{transform:rotate(45deg);color:#1D6B45}
          .faq-a{max-height:0;overflow:hidden;transition:max-height .35s ease,padding .35s ease;padding:0 0}
          .faq-a.open{max-height:300px;padding:0 0 18px}
          .faq-a-text{font-family:'Montserrat',sans-serif;font-size:13px;color:#6B6559;line-height:1.7;white-space:pre-line}
          @media(max-width:768px){
            .faq-q{font-size:13px;padding:14px 0}
            .faq-a-text{font-size:12px}
          }
        `}</style>
        <div className="container">
          <div className="section-header" style={{ textAlign: 'center' }}>
            <div className="section-eyebrow reveal">FAQ</div>
            <h2 className="section-title reveal">Common questions</h2>
          </div>
          <FaqSection />
          <div style={{ textAlign: 'center', marginTop: 28, fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: '#9B9487' }}>
            Have additional questions? Give us a call <a href="tel:+15163869923" style={{ color: '#1D6B45', fontWeight: 600, textDecoration: 'none' }}>(516) 386-9923</a>
          </div>
        </div>
      </section>

      {/* ━━━ FOOTER ━━━ */}
      <footer className="marketing-footer">
        <style>{`
          .marketing-footer{background:#1D6B45;padding:0;border-top:none;color:#fff;font-family:'Montserrat',sans-serif}

          /* ── CTA band ── */
          .mkt-footer-cta{padding:clamp(44px,5vw,64px) 0 clamp(40px,5vw,56px)}
          .mkt-footer-cta h2{font-family:'DM Serif Display',serif;font-size:clamp(36px,4.5vw,52px);line-height:1.05;color:#fff;font-weight:400;margin:0 0 14px;letter-spacing:-0.5px}
          .mkt-footer-cta-sub{font-family:'Montserrat',sans-serif;font-size:15px;color:rgba(255,255,255,0.75);line-height:1.5;margin:0 0 32px;max-width:620px}
          .mkt-footer-cta-buttons{display:flex;gap:14px;flex-wrap:wrap}
          .mkt-footer-cta-primary{display:inline-flex;align-items:center;gap:10px;background:#6dc171;color:#fff !important;font-family:'Montserrat',sans-serif;font-size:13px;font-weight:700;letter-spacing:1px;text-transform:uppercase;padding:16px 30px;border-radius:100px;text-decoration:none;transition:background .15s ease,transform .15s ease}
          .mkt-footer-cta-primary:hover{background:#5ab362;transform:translateY(-1px)}
          .mkt-footer-cta-secondary{display:inline-flex;align-items:center;gap:10px;background:transparent;color:#fff !important;font-family:'Montserrat',sans-serif;font-size:13px;font-weight:700;letter-spacing:1px;text-transform:uppercase;padding:16px 30px;border:1.5px solid rgba(255,255,255,0.40);border-radius:100px;text-decoration:none;transition:background .15s ease,border-color .15s ease}
          .mkt-footer-cta-secondary:hover{background:rgba(255,255,255,0.08);border-color:#fff}

          /* ── Columns grid ── */
          .mkt-footer-cols{padding:0 0 clamp(48px,6vw,72px);display:grid;grid-template-columns:1.3fr 1fr 1fr 1fr;gap:48px;align-items:start}

          /* Brand column */
          .mkt-footer-brand{display:flex;flex-direction:column;gap:20px;max-width:300px}
          .mkt-footer-brand-logo{display:inline-block}
          .mkt-footer-brand-logo img{height:28px;display:block}
          .mkt-footer-blurb{font-family:'Montserrat',sans-serif;font-size:14px;line-height:1.55;color:rgba(255,255,255,0.78);margin:0}
          .mkt-footer-rating{display:flex;align-items:center;gap:10px}
          .mkt-footer-rating-stars{display:inline-flex;gap:2px;color:#C8A55A}
          .mkt-footer-rating-text{font-family:'Montserrat',sans-serif;font-size:13px;color:rgba(255,255,255,0.78);font-weight:500}
          .mkt-footer-rating-text strong{color:#fff;font-weight:700}

          /* Link columns */
          .mkt-footer-col h4{font-family:'Montserrat',sans-serif;font-size:12px;font-weight:700;color:#A9CC8D;letter-spacing:2px;text-transform:uppercase;margin:0 0 22px}
          .mkt-footer-col ul{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:14px}
          .mkt-footer-col a{font-family:'Montserrat',sans-serif;font-size:15px;color:#fff !important;text-decoration:none;font-weight:400;transition:opacity .15s ease}
          .mkt-footer-col a:hover{opacity:0.7}

          /* Bottom strip */
          .mkt-footer-bottom{border-top:1px solid rgba(255,255,255,0.10);padding:22px 0;display:flex;justify-content:space-between;align-items:center;gap:20px;flex-wrap:wrap}
          .mkt-footer-meta{font-family:'Montserrat',sans-serif;font-size:13px;color:rgba(255,255,255,0.55);margin:0}
          .mkt-footer-tagline{font-family:'Montserrat',sans-serif;font-size:13px;color:rgba(255,255,255,0.55);margin:0}

          @media(max-width:960px){
            .mkt-footer-cols{grid-template-columns:1fr 1fr;gap:40px 32px}
            .mkt-footer-brand{grid-column:span 2;max-width:none}
          }
          @media(max-width:520px){
            .mkt-footer-cta{padding:36px 0 30px}
            .mkt-footer-cta-buttons{flex-direction:column;gap:10px;align-items:stretch}
            .mkt-footer-cta-primary,.mkt-footer-cta-secondary{justify-content:center;width:100%}
            .mkt-footer-cols{grid-template-columns:1fr;gap:36px;padding-left:24px;padding-right:24px}
            .mkt-footer-brand{grid-column:span 1}
            .mkt-footer-bottom{flex-direction:column;align-items:flex-start;gap:8px;padding:20px 24px}
            .mkt-footer-meta,.mkt-footer-tagline{font-size:12px}
            .mkt-footer-cta > .container{padding-left:24px;padding-right:24px}
          }
        `}</style>

        <div className="mkt-footer-cta">
          <div className="container">
            <h2>Got something that needs fixing?</h2>
            <p className="mkt-footer-cta-sub">
              Free written estimate. No trip charge. Usually booked within 48 hours.
            </p>
            <div className="mkt-footer-cta-buttons">
              <Link to="/estimate" className="mkt-footer-cta-primary">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2"/>
                  <path d="M16 2v4M8 2v4M3 10h18"/>
                </svg>
                Get a free estimate
              </Link>
              <a href="tel:+15163869923" className="mkt-footer-cta-secondary">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.33 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                Call (516) 386-9923
              </a>
            </div>
          </div>
        </div>

        <div className="container mkt-footer-cols">
          {/* Brand column */}
          <div className="mkt-footer-brand">
            <span className="mkt-footer-brand-logo">
              <img src={require('./assets/footer-logo.png')} alt="Reliable Handyman" />
            </span>
            <p className="mkt-footer-blurb">
              The handyman your neighbors already trust. Nassau County, NY.
            </p>
            <div className="mkt-footer-rating">
              <span className="mkt-footer-rating-stars" aria-label="4.8 stars">
                {[0,1,2,3,4].map(i => (
                  <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ))}
              </span>
              <span className="mkt-footer-rating-text">
                <strong>4.8</strong> avg for 10+ years
              </span>
            </div>
          </div>

          {/* SERVICES */}
          <div className="mkt-footer-col">
            <h4>Services</h4>
            <ul>
              <li><Link to="/services">Plumbing</Link></li>
              <li><Link to="/services">Electrical</Link></li>
              <li><Link to="/services">Drywall &amp; Paint</Link></li>
              <li><Link to="/services">Carpentry</Link></li>
              <li><Link to="/services">TV Mounting</Link></li>
              <li><Link to="/services">Furniture Assembly</Link></li>
            </ul>
          </div>

          {/* COMPANY */}
          <div className="mkt-footer-col">
            <h4>Company</h4>
            <ul>
              <li><a href="#reviews" onClick={(e) => scrollTo(e, 'reviews')}>Reviews</a></li>
              <li><a href="#faq" onClick={(e) => scrollTo(e, 'faq')}>FAQ</a></li>
              <li><Link to="/privacy">Privacy</Link></li>
              <li><Link to="/terms">Terms</Link></li>
            </ul>
          </div>

          {/* GET STARTED */}
          <div className="mkt-footer-col">
            <h4>Get Started</h4>
            <ul>
              <li><Link to="/book">Book a Visit</Link></li>
              <li><Link to="/estimate">Free Estimate</Link></li>
              <li><Link to="/services">Flat-Rate Pricing</Link></li>
            </ul>
          </div>
        </div>

        <div className="container mkt-footer-bottom">
          <p className="mkt-footer-meta">&copy; 2026 Reliable Handyman LLC &middot; Licensed &amp; insured</p>
          <p className="mkt-footer-tagline">No trip charge &middot; No markup on parts &middot; The estimate is the price</p>
        </div>
      </footer>
    </div>
  );
}
